import { BulkUploadModal, ValidationError, BulkUploadResult, LanguageColumnMapping } from '@/components/common/BulkUploadModal';
import { Button, Typography, Modal, Progress, Row, Col, message } from 'antd';
import { useState } from 'react';
import { Resident } from '@/types/resources/resident';
import { useAuth } from '@/contexts/AuthContext';
import { addResidentsBulkWithUsers } from '@/api/residents';
import { useTranslation } from 'react-i18next';
import { downloadResidentsTemplate } from '@/templates/csv/dynamicTemplateGenerator';
import validator from 'validator';

const { Title, Text } = Typography;


interface ResidentsBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function ResidentsBulkUploadModal({ visible, onCancel, onSuccess }: ResidentsBulkUploadModalProps) {
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const { t, i18n } = useTranslation(['residents', 'common']);
  const currentLanguage = i18n.language as 'en' | 'es' | 'pt';
  
  // State for managing the flow
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'creating'>('upload');
  const [processedData, setProcessedData] = useState<Array<{
    resident: Omit<Resident, 'id' | 'workspace' | 'activityRecords'>;
  }>>([]);

  // Sample data for template generation (will be replaced by CSV template)
  const sampleData: any[] = [];

  // Column definitions for the template (internationalized) - matching ResidentFormModal
  const columns = [
    { key: 'apartmentUnitBlock', title: t('residents:fields.apartmentUnitBlock'), required: false, hidden: true, readOnly: true }, // Auto-populated, hidden from field info, read-only in table
    { key: 'residentName', title: t('residents:fields.residentName'), required: true },
    { key: 'phoneNumber', title: t('residents:fields.phone'), required: true },
    { key: 'email', title: t('residents:fields.email'), required: true },
    { key: 'notes', title: t('residents:fields.notes'), required: false }
  ];

  // Language-aware column mappings for CSV headers
  const columnMappings: LanguageColumnMapping = {
    en: {
      apartmentUnitBlock: ['apartmentunitblock', 'apartment unit block', 'unit block', 'door number', 'doornumber', 'apartment unit', 'apartmentunit'],
      residentName: ['residentname', 'resident name', 'name', 'full name', 'fullname'],
      phoneNumber: ['phonenumber', 'phone number', 'phone', 'mobile', 'contact'],
      email: ['email', 'email address', 'emailaddress'],
      notes: ['notes', 'note', 'comments', 'comment', 'description']
    },
    es: {
      apartmentUnitBlock: ['unidadapartamento', 'unidad apartamento', 'unidad bloque', 'numero puerta', 'numeropuerta', 'apartamento', 'unidad'],
      residentName: ['nombreresidente', 'nombre residente', 'nombre', 'nombre completo', 'nombrecompleto'],
      phoneNumber: ['numerotelefono', 'numero telefono', 'telefono', 'movil', 'contacto'],
      email: ['email', 'correo electronico', 'correoelectronico'],
      notes: ['notas', 'nota', 'comentarios', 'comentario', 'descripcion']
    },
    pt: {
      apartmentUnitBlock: ['unidadeapartamento', 'unidade apartamento', 'unidade bloco', 'numero porta', 'numeroporta', 'apartamento', 'unidade'],
      residentName: ['nomemorador', 'nome morador', 'nome', 'nome completo', 'nomecompleto'],
      phoneNumber: ['numerotelefone', 'numero telefone', 'telefone', 'celular', 'contato'],
      email: ['email', 'endereco de email', 'enderecodeemail'],
      notes: ['notas', 'nota', 'comentarios', 'comentario', 'descricao']
    }
  };

  // Validation function for resident records - matching ResidentFormModal validation
  const validateResidentRecord = (record: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Required field validation
    if (!record.residentName || record.residentName.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'residentName',
        value: record.residentName,
        message: t('residents:validation.residentNameRequired')
      });
    } else if (record.residentName.length > 100) {
      errors.push({
        row: rowIndex,
        field: 'residentName',
        value: record.residentName,
        message: t('residents:validation.residentNameTooLong')
      });
    }

    if (!record.phoneNumber || record.phoneNumber.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'phoneNumber',
        value: record.phoneNumber,
        message: t('residents:validation.phoneNumberRequired')
      });
    } else {
      const phone = record.phoneNumber.trim();
      
      // Use validator library for phone validation
      if (!validator.isMobilePhone(phone, 'any', { strictMode: false })) {
        errors.push({
          row: rowIndex,
          field: 'phoneNumber',
          value: record.phoneNumber,
          message: t('residents:validation.phoneNumberInvalid')
        });
      } else if (phone.length > 20) {
        errors.push({
          row: rowIndex,
          field: 'phoneNumber',
          value: record.phoneNumber,
          message: t('residents:validation.phoneNumberTooLong')
        });
      }
    }

    if (!record.email || record.email.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'email',
        value: record.email,
        message: t('residents:validation.emailRequired')
      });
    } else {
      const email = record.email.trim();
      
      // Use validator library for email validation
      if (!validator.isEmail(email)) {
        errors.push({
          row: rowIndex,
          field: 'email',
          value: record.email,
          message: t('residents:validation.emailInvalid')
        });
      } else if (email.length > 254) {
        errors.push({
          row: rowIndex,
          field: 'email',
          value: record.email,
          message: t('residents:validation.emailTooLong')
        });
      }
    }

    // Notes validation (optional)
    if (record.notes !== undefined && record.notes !== '' && record.notes.length > 500) {
      errors.push({
        row: rowIndex,
        field: 'notes',
        value: record.notes,
        message: t('residents:validation.notesTooLong')
      });
    }

    return errors;
  };

  // Transform CSV record to Resident type - matching ResidentFormModal
  const transformResidentRecord = (record: any): Omit<Resident, 'id' | 'workspace' | 'activityRecords'> => {
    return {
      user: {
        id: '', // Will be generated by the backend
        displayName: record.residentName.trim(),
        phoneNumber: record.phoneNumber.trim(),
        email: record.email.trim()
      },
      notes: record.notes?.trim() || '',
      type: 'owner',
      status: 'pending',
      apartmentUnit: {
        id: record.apartmentUnitId,
        number: record.apartmentUnitNumber
      },
      block: {
        id: record.blockId,
        name: record.blockName
      },
      moveInDate: new Date(record.moveInDate || new Date().toISOString().split('T')[0]),
      contacts: [] // Initialize empty contacts array
    };
  };

  // Custom data processor to create combined field for display
  const processDataForDisplay = (uploadResult: BulkUploadResult<any>) => {
    if (!uploadResult || !(uploadResult as any).originalValidRecords) return [];
    
    return (uploadResult as any).originalValidRecords.map((record: any, index: number) => {
      // Check if this row has validation errors
      const rowErrors = uploadResult.errors.filter(e => e.row === index + 1);
      const hasErrors = rowErrors.length > 0;
      
      // Create combined apartment unit and block name field
      const apartmentUnitBlock = record.apartmentUnitNumber && record.blockName 
        ? `${record.apartmentUnitNumber} - ${record.blockName}`
        : record.apartmentUnitBlock || '';

      return {
        ...record,
        apartmentUnitBlock, // Add the combined field
        rowNumber: index + 1,
        isErrorRow: hasErrors,
        errorCount: rowErrors.length
      };
    });
  };

  // Handle dynamic template download
  const handleDownloadTemplate = async () => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      await downloadResidentsTemplate(
        firestoreUser as any,
        currentLanguage,
        `residents_template_${currentLanguage}.csv`
      );
      message.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      message.error('Failed to download template. Please try again.');
    }
  };

  // Handle bulk upload confirmation - now shows preview first
  const handleBulkUploadConfirm = async (result: BulkUploadResult<Omit<Resident, 'id' | 'workspace' | 'activityRecords'>>) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      // Process the data to show preview
      const processedData = result.validRecords.map(residentData => {
        return {
          resident: residentData
        };
      });

      setProcessedData(processedData);
      setCurrentStep('preview');

    } catch (error) {
      console.error('Error processing data:', error);
      message.error('Error processing data. Please try again.');
    }
  };

  // Handle the actual creation after confirmation
  const handleConfirmCreation = async () => {
    if (!firestoreUser || processedData.length === 0) {
      message.error('No data to process');
      return;
    }

    try {
      setCurrentStep('creating');
      
      const totalRecords = processedData.length;
      let currentProgress = 0;
      
      // Show progress modal
      const progressModal = Modal.info({
        title: 'Creating Residents',
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${currentProgress}/${totalRecords} (${percent}%)`}
            />
            <p>Creating users...</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Use the new efficient batch function
      const result = await addResidentsBulkWithUsers(
        processedData.map(data => data.resident),
        firestoreUser,
        (phase, uploaded, total) => {
          currentProgress = uploaded;
          const percent = Math.round((uploaded / total) * 100);
          
          let phaseText = '';
          switch (phase) {
            case 'users':
              phaseText = 'Creating user accounts...';
              break;
            case 'residents':
              phaseText = 'Creating resident records...';
              break;
            case 'apartmentUnits':
              phaseText = 'Updating apartment units...';
              break;
            default:
              phaseText = 'Processing...';
          }
          
          progressModal.update({
            content: (
              <div>
                <Progress 
                  percent={percent} 
                  status="active" 
                  format={(percent) => `${uploaded}/${total} (${percent}%)`}
                />
                <p>{phaseText}</p>
                <p>Progress: {uploaded} of {total}</p>
              </div>
            )
          });
        }
      );

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      
      if (result.errorCount > 0) {
        message.warning(`Created ${result.successCount} residents with ${result.errorCount} errors. Check console for details.`);
        console.error('Bulk upload errors:', result.errors);
      } else {
        message.success(`Successfully created ${result.successCount} residents`);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Bulk upload error:', error);
      message.error('Error creating residents. Please try again.');
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentStep('upload');
    setProcessedData([]);
    onCancel();
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <BulkUploadModal
            visible={visible}
            onCancel={handleModalCancel}
            onConfirm={handleBulkUploadConfirm}
            title={t('residents:actions.bulkUpload')}
            onDownloadTemplate={handleDownloadTemplate}
            templateFileName={`residents_template_${currentLanguage}.csv`}
            validateRecord={validateResidentRecord}
            transformRecord={transformResidentRecord}
            columns={columns}
            sampleData={sampleData}
            accept=".csv"
            maxFileSize={5}
            language={currentLanguage}
            columnMappings={columnMappings}
            customDataProcessor={processDataForDisplay}
          />
        );

      case 'preview':
        return (
          <Modal
            title={t('residents:bulkUpload.title')}
            open={visible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="cancel" onClick={handleModalCancel}>
                {t('common:actions.cancel')}
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
                {t('common:actions.confirm')}
              </Button>
            ]}
            width={800}
          >
            <div style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  {t('residents:messages.creationSummary.title')}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  {t('residents:messages.creationSummary.subtitle')}
                </Text>
              </div>

              {/* Overall Stats Summary */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '20px'
              }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                        {processedData.length}
                      </div>
                      <div style={{ fontSize: '12px', color: '#52c41a' }}>
                        Total Residents
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                        {processedData.filter(data => data.resident.apartmentUnit?.number).length}
                      </div>
                      <div style={{ fontSize: '12px', color: '#1890ff' }}>
                        With Units Assigned
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Individual Resident Cards - 2 per row */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '20px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '24px'
              }}>
                <Row gutter={[16, 16]}>
                  {processedData.map((data: any, index: number) => (
                    <Col key={index} xs={24} sm={12}>
                      <div style={{ 
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        height: '100%'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            backgroundColor: '#1890ff', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            marginRight: '12px'
                          }}>
                            <span style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                              {data.resident.user?.displayName?.charAt(0)?.toUpperCase() || 'R'}
                            </span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Title level={5} style={{ margin: 0, color: '#1890ff', fontSize: '14px' }}>
                              {data.resident.user?.displayName || 'Unknown'}
                            </Title>
                            <Text style={{ margin: 0, color: '#666', fontSize: '11px' }}>
                              {data.resident.user?.email || 'No email'}
                            </Text>
                          </div>
                        </div>

                        <Row gutter={[8, 8]}>
                          <Col xs={24} sm={8}>
                            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f6ffed', borderRadius: '4px', border: '1px solid #b7eb8f' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>
                                {data.resident.user?.phoneNumber || 'N/A'}
                              </div>
                              <div style={{ fontSize: '10px', color: '#52c41a' }}>
                                {t('residents:fields.phone')}
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={8}>
                            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
                                {data.resident.apartmentUnit?.number || 'N/A'}
                              </div>
                              <div style={{ fontSize: '10px', color: '#1890ff' }}>
                                {t('residents:fields.unit')}
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={8}>
                            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff7e6', borderRadius: '4px', border: '1px solid #ffd591' }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fa8c16' }}>
                                {data.resident.block?.name || 'N/A'}
                              </div>
                              <div style={{ fontSize: '10px', color: '#fa8c16' }}>
                                {t('residents:fields.location.block')}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Confirmation Message */}
              <div style={{ 
                marginTop: '20px', 
                padding: '16px', 
                backgroundColor: '#fff2e8', 
                borderRadius: '6px', 
                border: '1px solid #ffbb96',
                textAlign: 'center'
              }}>
                <Text style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                  {t('residents:messages.creationSummary.confirmationMessage', {
                    count: processedData.length
                  })}
                </Text>
              </div>
            </div>
          </Modal>
        );

      case 'creating':
        return (
          <Modal
            title="Creating Residents..."
            open={visible}
            onCancel={handleModalCancel}
            footer={null}
            width={600}
          >
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Progress type="circle" percent={100} status="active" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Please wait while the residents are being created...
              </Text>
            </div>
          </Modal>
        );

      default:
        return null;
    }
  };

  return renderContent();
}

// Export the content component for use in cards
export function ResidentsBulkUploadContent({ onSuccess }: { onSuccess: () => void }) {
  return (
    <ResidentsBulkUploadModal
      visible={true}
      onCancel={() => {}}
      onSuccess={onSuccess}
    />
  );
}